<template>
	<div class="col-span-full h-full flex flex-col overflow-y-auto [scrollbar-gutter:stable]">

		<slot name="header">
			<!-- Default 'go back' button (through router history) -->
			<div class="flex items-center px-6 pt-6 flex-shrink-0">
				<button class="btn btn-ghost btn-sm capitalize" @click="$router.back()">
					<ArrowLeftIcon class="w-4 h-4"/>
					{{ $t('Return') }}
				</button>
			</div>
		</slot>

		<div class="p-6 flex flex-col pb-10">
			<div>
				<div class="mb-2">
					<p class="font-semibold text-lg">
						{{ title }}
					</p>
					<div class="h-1.5 w-16 rounded-full" :class="`bg-${accentColor}`"></div>
				</div>
				<div class="bg-white border rounded-lg p-4 overflow-y-auto" :class="`border-${accentColor}`">
					<div v-if="groupAuthors.length === 0" class="text-sm py-4">
						<p>{{ emptyMessage }}</p>
					</div>
					<div v-else class="grid grid-cols-1 @md:grid-cols-3 gap-4">
						<button
							v-for="authorData in groupAuthors"
							:key="authorData.author.id"
							type="button"
							class="group inline-flex items-center rounded cursor-pointer border-none bg-white hover:bg-base-100 h-12 py-2 justify-between text-left min-h-fit"
							@click="$emit('selectAuthor', authorData.author.id, authorData.section)"
						>
							<div class="flex items-center gap-4 text-left px-2">
								<UserAvatar :user="authorData.author" :usePublicProfile="true" class="w-20 h-20 group-hover:bg-base-200" />
								<div class="flex-1">
									<div class="flex items-center justify-between line-clamp-2 break-words leading-tight">
										<span class="block font-semibold">{{ authorData.author.publicName ?? authorData.author.name }}</span>
									</div>
									<div class="text-xs text-base-content-light lowercase block truncate break-words hyphens-auto mt-0.5">
										{{ $t('{count} lesson plans', { count: authorData.totalPlanCount }) }}
									</div>
								</div>
							</div>
							<div class="flex items-center ml-2 pr-2">
								<ChevronRightIcon class="w-5 h-5 text-base-content/40" />
							</div>
						</button>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script lang="ts">
import { authorGroupProperties, useLessonPlanCollectionsFromColleaguesStore, usePublicLessonPlanCollectionsStore } from '@/stores/DiscoverCollections.store';
import { ChevronRightIcon, ArrowLeftIcon } from '@heroicons/vue/24/outline';
import LessonPlanDiscoverAuthor from '@/components/LessonPlanDiscoverAuthor.vue';
import UserAvatar from '@/components/UserAvatar.vue';


export default {

	name: 'LessonPlanDiscoverAuthorGroup',
	emits: ['selectAuthorGroup', 'selectAuthor'],

	components: {
		ChevronRightIcon,
		ArrowLeftIcon,
		LessonPlanDiscoverAuthor,
		UserAvatar,
	},

	props: {
		groupId: {
			type: String,
			required: true,
		},
	},

	data() {
		return {
			authorGroupProperties,
		};
	},

	computed: {
		collectionsFromColleaguesStore() {
			return useLessonPlanCollectionsFromColleaguesStore();
		},

		publicCollectionsStore() {
			return usePublicLessonPlanCollectionsStore();
		},

		title(): string {
			const i18nKey = authorGroupProperties[this.groupId]?.titleI18nKey;
			if (!i18nKey) {
				return '';
			}
			return this.$t(i18nKey, 2);
		},

		// all authors (and some metata info like total plan count) for the selected group
		groupAuthors() {
			switch(this.groupId) {
				case 'colleagues':
					return this.collectionsFromColleaguesStore.groupedByAuthor;
				case 'publishers':
					return this.publicCollectionsStore.groupedByAuthor;
				default:
					return [];
			}
		},

		accentColor(): string {
			return authorGroupProperties[this.groupId]?.accentColor || 'neutral';
		},

		emptyMessage(): string {
			return authorGroupProperties[this.groupId]?.emptyMessageI18nKey
				? this.$t(authorGroupProperties[this.groupId].emptyMessageI18nKey)
				: '';
		},
	},

};
</script>
